import { useSyncExternalStore } from "react";
import { areLogicalProjectIdentitiesEquivalent } from "./projectIdentity";
import {
  SAVE_PERSISTED_EVENT,
  SAVE_PERSIST_FAILED_EVENT,
  dispatchSavePersistFailed,
  type SaveFailedEventDetail,
  type SavePersistedEventDetail,
} from "./saveEvents";
import { isUnityRuntimeReadyAtLeast } from "./unityRuntimeBridgeState";
import { resolveSaveBarrier } from "./saveBarrier";

// Unity packing, signed upload, and Main API job completion can legitimately take
// several minutes for large projects. This is intentionally a last-resort UI
// recovery boundary rather than an ordinary request timeout.
export const PROJECT_SAVE_UI_WATCHDOG_TIMEOUT_MS = 15 * 60_000;

export type ProjectSaveUiStatus = "idle" | "saving" | "failed";

export interface ProjectSaveUiOperation {
  projectPath: string;
  saveAttemptId: string;
  reason: string;
  /** 저장 요청 시점의 source Space. 완료 후 active Space가 바뀌어도 Toast 문구가 흔들리지 않는다. */
  sourceSpaceName?: string;
  startedAt: number;
  /**
   * 저장 중 전체 화면 blocker로 Studio 입력을 막는지.
   *
   * `false`인 background 저장은 `status`를 `saving`으로 만들지 않는다. 따라서
   * `EditorSaveBlockingOverlay`도 뜨지 않고 Home·Space 전환·Import도 막히지 않는다.
   * 다만 ownership 판정에는 그대로 참여하므로 그 저장의 terminal은 dirty 해제와
   * 실패 toast를 정상적으로 트리거한다.
   */
  blocking: boolean;
}

export interface ProjectSaveUiState {
  status: ProjectSaveUiStatus;
  activeOperation: ProjectSaveUiOperation | null;
  lastSettledOperation: ProjectSaveUiOperation | null;
  lastError: string | null;
}

const idleState: ProjectSaveUiState = {
  status: "idle",
  activeOperation: null,
  lastSettledOperation: null,
  lastError: null,
};

let state = idleState;
let listenersInstalled = false;
let lastSettledEventDetail:
  | SavePersistedEventDetail
  | SaveFailedEventDetail
  | null = null;
let activeWatchdogTimer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

/**
 * 진행 중인 background 저장들. blocking 저장과 slot을 공유하지 않는다.
 *
 * Add Space의 package 저장처럼 UI를 잠그지 않는 저장이 여기에 들어간다. 사용자가
 * 그 사이에 상단 Save를 눌러도 blocking slot은 비어 있으므로 정상 동작한다.
 */
const backgroundOperations = new Map<string, ProjectSaveUiOperation>();
const backgroundWatchdogTimers = new Map<
  string,
  ReturnType<typeof setTimeout>
>();
let lastSettledBackgroundOperation: ProjectSaveUiOperation | null = null;
let lastSettledBackgroundEventDetail:
  | SavePersistedEventDetail
  | SaveFailedEventDetail
  | null = null;

const clearBackgroundWatchdog = (saveAttemptId: string): void => {
  const timer = backgroundWatchdogTimers.get(saveAttemptId);
  if (timer !== undefined) {
    clearTimeout(timer);
    backgroundWatchdogTimers.delete(saveAttemptId);
  }
};

const findBackgroundOperationForDetail = (
  detail: SavePersistedEventDetail | SaveFailedEventDetail | null | undefined,
): ProjectSaveUiOperation | null => {
  const saveAttemptId = detail?.saveAttemptId?.trim();
  if (!saveAttemptId) return null;
  const operation = backgroundOperations.get(saveAttemptId);
  if (!operation) return null;
  return doesDetailMatchOperation(detail, operation) ? operation : null;
};

/**
 * saveAttemptId 없이 도착한 저장 실패를 귀속시킬 background 저장.
 *
 * Unity는 `reactToUnity_saveJson` snapshot을 한 번에 하나만 처리하고,
 * `unityToReact_errorOccurred`의 payload에는 `saveAttemptId`가 실려 오지 않는다.
 * 따라서 blocking 저장이 없고 background 저장만 떠 있으면 그 실패는 그 저장의
 * 것이다. 여러 건이 겹치면 Unity가 먼저 집어간 것이 실패한 것이므로 가장 먼저
 * 시작한 저장을 고른다.
 */
const findOldestInFlightBackgroundOperation = (): ProjectSaveUiOperation | null => {
  let oldest: ProjectSaveUiOperation | null = null;
  backgroundOperations.forEach((operation) => {
    if (oldest === null || operation.startedAt < oldest.startedAt) {
      oldest = operation;
    }
  });
  return oldest;
};

const clearActiveWatchdog = () => {
  if (activeWatchdogTimer !== null) {
    clearTimeout(activeWatchdogTimer);
    activeWatchdogTimer = null;
  }
};

const emit = () => {
  listeners.forEach((listener) => listener());
};

const setState = (nextState: ProjectSaveUiState) => {
  state = nextState;
  emit();
};

const doesDetailMatchOperation = (
  detail: SavePersistedEventDetail | SaveFailedEventDetail | null | undefined,
  operation: ProjectSaveUiOperation | null,
): boolean => {
  if (!detail || !operation || !detail.saveAttemptId) return false;
  if (detail.saveAttemptId !== operation.saveAttemptId) return false;

  const detailPath = detail.gcsPath?.trim();
  const operationPath = operation.projectPath.trim();
  if (!detailPath || !operationPath) return false;

  return (
    detailPath === operationPath ||
    areLogicalProjectIdentitiesEquivalent(detailPath, operationPath)
  );
};

const settleFromEvent = (
  status: "persisted" | "failed",
  detail: SavePersistedEventDetail | SaveFailedEventDetail,
) => {
  const backgroundOperation = findBackgroundOperationForDetail(detail);
  if (backgroundOperation) {
    clearBackgroundWatchdog(backgroundOperation.saveAttemptId);
    backgroundOperations.delete(backgroundOperation.saveAttemptId);
    lastSettledBackgroundOperation = backgroundOperation;
    lastSettledBackgroundEventDetail = detail;
    // background 저장은 blocker를 띄우지 않았으므로 status도 바꾸지 않는다.
    emit();
    return;
  }

  const activeOperation = state.activeOperation;
  if (!doesDetailMatchOperation(detail, activeOperation)) return;
  clearActiveWatchdog();
  lastSettledEventDetail = detail;

  setState({
    status: status === "failed" ? "failed" : "idle",
    activeOperation: null,
    lastSettledOperation: activeOperation,
    lastError:
      status === "failed"
        ? (detail as SaveFailedEventDetail).error?.trim() || "Save failed"
        : null,
  });
};

const ensureListeners = () => {
  if (listenersInstalled || typeof window === "undefined") return;
  listenersInstalled = true;
  window.addEventListener(SAVE_PERSISTED_EVENT, ((event: CustomEvent) => {
    settleFromEvent("persisted", event.detail ?? {});
  }) as EventListener);
  window.addEventListener(SAVE_PERSIST_FAILED_EVENT, ((event: CustomEvent) => {
    settleFromEvent("failed", event.detail ?? {});
  }) as EventListener);
};

export const beginProjectSaveUiOperation = ({
  projectPath,
  saveAttemptId,
  reason,
  sourceSpaceName,
  blocking = true,
  watchdogTimeoutMs = PROJECT_SAVE_UI_WATCHDOG_TIMEOUT_MS,
}: {
  projectPath?: string | null;
  saveAttemptId: string;
  reason: string;
  sourceSpaceName?: string | null;
  /** `false`이면 blocker 없이 진행하되 ownership 판정에는 참여한다. */
  blocking?: boolean;
  watchdogTimeoutMs?: number;
}): boolean => {
  const normalizedAttemptId = saveAttemptId.trim();
  if (!normalizedAttemptId || !isUnityRuntimeReadyAtLeast("scene_ready")) {
    return false;
  }

  const resolvedWatchdogTimeoutMs =
    Number.isFinite(watchdogTimeoutMs) && watchdogTimeoutMs > 0
      ? watchdogTimeoutMs
      : PROJECT_SAVE_UI_WATCHDOG_TIMEOUT_MS;

  if (!blocking) {
    if (backgroundOperations.has(normalizedAttemptId)) {
      return false;
    }
    ensureListeners();
    const backgroundOperation: ProjectSaveUiOperation = {
      projectPath: projectPath?.trim() ?? "",
      saveAttemptId: normalizedAttemptId,
      reason: reason.trim(),
      ...(sourceSpaceName?.trim()
        ? { sourceSpaceName: sourceSpaceName.trim() }
        : {}),
      startedAt: Date.now(),
      blocking: false,
    };
    backgroundOperations.set(normalizedAttemptId, backgroundOperation);
    backgroundWatchdogTimers.set(
      normalizedAttemptId,
      setTimeout(() => {
        dispatchActiveProjectSaveUiFailure({
          gcsPath: backgroundOperation.projectPath,
          saveAttemptId: backgroundOperation.saveAttemptId,
          error: "Save timed out before package persistence completed",
        });
      }, resolvedWatchdogTimeoutMs),
    );
    emit();
    return true;
  }

  if (state.status === "saving") {
    return false;
  }

  ensureListeners();
  clearActiveWatchdog();
  lastSettledEventDetail = null;
  const activeOperation: ProjectSaveUiOperation = {
    projectPath: projectPath?.trim() ?? "",
    saveAttemptId: normalizedAttemptId,
    reason: reason.trim(),
    ...(sourceSpaceName?.trim()
      ? { sourceSpaceName: sourceSpaceName.trim() }
      : {}),
    startedAt: Date.now(),
    blocking: true,
  };
  setState({
    status: "saving",
    activeOperation,
    lastSettledOperation: null,
    lastError: null,
  });
  activeWatchdogTimer = setTimeout(() => {
    dispatchActiveProjectSaveUiFailure({
      gcsPath: activeOperation.projectPath,
      saveAttemptId: activeOperation.saveAttemptId,
      error: "Save timed out before package persistence completed",
    });
  }, resolvedWatchdogTimeoutMs);
  return true;
};

export function dispatchActiveProjectSaveUiFailure(
  detail: SaveFailedEventDetail = {},
  {
    allowUntargetedBackgroundFallback = true,
  }: {
    /**
     * `saveAttemptId`가 없는 실패를 진행 중인 background 저장에 귀속시킬지.
     *
     * 저장과 무관한 Unity 에러(Space 전환 실패 등)를 Add Space 저장의 실패로
     * 오인하지 않도록 호출자가 끌 수 있다.
     */
    allowUntargetedBackgroundFallback?: boolean;
  } = {},
): boolean {
  const incomingAttemptId = detail.saveAttemptId?.trim();
  const backgroundOperation = incomingAttemptId
    ? backgroundOperations.get(incomingAttemptId) ?? null
    : allowUntargetedBackgroundFallback && state.activeOperation === null
      ? findOldestInFlightBackgroundOperation()
      : null;
  const activeOperation = backgroundOperation ?? state.activeOperation;
  if (!activeOperation) return false;

  if (
    incomingAttemptId &&
    incomingAttemptId !== activeOperation.saveAttemptId
  ) {
    return false;
  }

  const incomingPath = detail.gcsPath?.trim();
  const activePath = activeOperation.projectPath.trim();
  if (
    incomingPath &&
    activePath &&
    incomingPath !== activePath &&
    !areLogicalProjectIdentitiesEquivalent(incomingPath, activePath)
  ) {
    return false;
  }

  const canonicalDetail: SaveFailedEventDetail = {
    ...detail,
    gcsPath: activePath || incomingPath,
    saveAttemptId: activeOperation.saveAttemptId,
    reason: detail.reason?.trim() || activeOperation.reason,
    error: detail.error?.trim() || "Save failed",
    timestamp: detail.timestamp ?? Date.now(),
  };
  resolveSaveBarrier(canonicalDetail);
  dispatchSavePersistFailed(canonicalDetail);
  return true;
}

export const failProjectSaveUiOperation = (
  saveAttemptId: string,
  error?: unknown,
): void => {
  dispatchActiveProjectSaveUiFailure({
    saveAttemptId: saveAttemptId.trim(),
    error:
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "Save failed",
  });
};

export const isProjectSaveUiActive = (): boolean => state.status === "saving";

export const getProjectSaveUiState = (): ProjectSaveUiState => state;

/** 현재 또는 방금 settle된 owned 저장과 terminal event를 다시 결합한다. */
export const getOwnedProjectSaveUiOperation = (
  detail: SavePersistedEventDetail | SaveFailedEventDetail | null | undefined,
): ProjectSaveUiOperation | null => {
  if (doesDetailMatchOperation(detail, state.activeOperation)) {
    return state.activeOperation;
  }
  const backgroundOperation = findBackgroundOperationForDetail(detail);
  if (backgroundOperation) return backgroundOperation;
  if (
    detail === lastSettledBackgroundEventDetail &&
    doesDetailMatchOperation(detail, lastSettledBackgroundOperation)
  ) {
    return lastSettledBackgroundOperation;
  }
  if (
    detail === lastSettledEventDetail &&
    doesDetailMatchOperation(detail, state.lastSettledOperation)
  ) {
    return state.lastSettledOperation;
  }
  return null;
};

export const doesProjectSaveUiEventMatchOwnedOperation = (
  detail: SavePersistedEventDetail | SaveFailedEventDetail | null | undefined,
): boolean => {
  if (doesDetailMatchOperation(detail, state.activeOperation)) return true;
  if (findBackgroundOperationForDetail(detail)) return true;
  if (
    detail === lastSettledBackgroundEventDetail &&
    doesDetailMatchOperation(detail, lastSettledBackgroundOperation)
  ) {
    return true;
  }
  return (
    detail === lastSettledEventDetail &&
    doesDetailMatchOperation(detail, state.lastSettledOperation)
  );
};

export const subscribeProjectSaveUiState = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useProjectSaveUiState = (): ProjectSaveUiState =>
  useSyncExternalStore(
    subscribeProjectSaveUiState,
    getProjectSaveUiState,
    getProjectSaveUiState,
  );

/**
 * Editor 세션이 끝날 때 호출한다. 이 모듈의 상태는 모듈 수준이라 Editor를 unmount해도
 * 살아남는다. 그대로 두면 이전 세션의 blocker·watchdog이 새 Editor에서 그대로 이어져
 * `Saving your space…`가 남고, 이전 화면의 타이머가 새 화면에 `Save failed`를 띄운다.
 *
 * 진행 중이던 durable 저장(Revision commit HTTP)은 끊지 않는다. 그 결과 event는
 * owned operation이 없어 어느 UI도 바꾸지 않는다 (STUDIO-PACKAGE-001 이탈·재진입).
 */
export const resetProjectSaveUiSession = (): void => {
  clearActiveWatchdog();
  backgroundWatchdogTimers.forEach((timer) => clearTimeout(timer));
  backgroundWatchdogTimers.clear();
  backgroundOperations.clear();
  lastSettledBackgroundOperation = null;
  lastSettledBackgroundEventDetail = null;
  state = idleState;
  lastSettledEventDetail = null;
  emit();
};

export const resetProjectSaveUiRuntimeForTests = resetProjectSaveUiSession;
